/**
 * Pure helpers that make request data safe and small enough to attach to an
 * issue report. No Angular imports, so they can be checked in plain Node.
 */

const SENSITIVE_KEY =
  /^(authorization|auth|cookie|session)$|pass(word|wd)?|secret|token|api[-_]?key|auth[-_]?key|credential|cvv|card[-_]?number|iban/i;

export const REDACTED = '[redacted]';
const MAX_DEPTH = 8;

export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY.test(key);
}

/** Replaces sensitive query-param values; keeps the URL otherwise intact. */
export function redactUrl(url: string): string {
  const queryStart = url.indexOf('?');
  if (queryStart === -1) return url;

  const hashStart = url.indexOf('#', queryStart);
  const query =
    hashStart === -1
      ? url.slice(queryStart + 1)
      : url.slice(queryStart + 1, hashStart);
  const hash = hashStart === -1 ? '' : url.slice(hashStart);

  const redacted = query
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return pair;
      let key = pair.slice(0, eq);
      try {
        key = decodeURIComponent(key);
      } catch {}
      return isSensitiveKey(key) ? `${pair.slice(0, eq)}=${REDACTED}` : pair;
    })
    .join('&');

  return `${url.slice(0, queryStart)}?${redacted}${hash}`;
}

export function redactDeep(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return '[…]';
  if (seen.has(value)) return '[circular]';
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactDeep(item, depth + 1, seen));
  }

  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    out[key] = isSensitiveKey(key)
      ? REDACTED
      : redactDeep(child, depth + 1, seen);
  }
  return out;
}

export function truncate(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}… [truncated, ${text.length} chars]`;
}

/**
 * Turns a request/response body into a short, redacted string.
 * Binary bodies are described rather than copied.
 */
export function serializeBody(
  body: unknown,
  maxChars: number,
): string | undefined {
  if (body === null || body === undefined || body === '') return undefined;

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const fields: string[] = [];
    body.forEach((v, k) => {
      if (isSensitiveKey(k)) fields.push(`${k}=${REDACTED}`);
      else if (typeof v === 'string') fields.push(`${k}=${truncate(v, 80)}`);
      else fields.push(`${k}=[file ${(v as File).name || ''}]`);
    });
    return truncate(`[FormData] ${fields.join(', ')}`, maxChars);
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return `[Blob ${body.type || 'binary'}, ${body.size} bytes]`;
  }
  if (body instanceof ArrayBuffer) {
    return `[ArrayBuffer ${body.byteLength} bytes]`;
  }
  if (
    typeof URLSearchParams !== 'undefined' &&
    body instanceof URLSearchParams
  ) {
    return truncate(redactUrl(`?${body.toString()}`).slice(1), maxChars);
  }

  if (typeof body === 'string') {
    try {
      return truncate(JSON.stringify(redactDeep(JSON.parse(body))), maxChars);
    } catch {
      return truncate(body, maxChars);
    }
  }

  try {
    return truncate(JSON.stringify(redactDeep(body)), maxChars);
  } catch {
    return '[unserializable body]';
  }
}
